import { useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useActiveMenuGroup, type ActiveMenuChild } from '@/hooks/useActiveMenuGroup';

interface NavTab {
    key: string;
    path: string;
    label: ReactNode;
}

function getSpecialTabs(pathname: string): { tabs: NavTab[]; activePath: string } | null {
    if (pathname.startsWith('/buyback') || pathname === ROUTES.reports.buyback) {
        return {
            tabs: [
                { key: ROUTES.buyback.list, path: ROUTES.buyback.list, label: 'Quản lý đơn mua vàng cũ' },
                { key: ROUTES.buyback.new, path: ROUTES.buyback.new, label: 'Tạo đơn mua vàng cũ' },
                // { key: ROUTES.reports.buyback, path: ROUTES.reports.buyback, label: 'Báo cáo mua vàng cũ' },
            ],
            activePath: pathname === ROUTES.buyback.new
                ? ROUTES.buyback.new
                : pathname === ROUTES.reports.buyback
                    ? ROUTES.reports.buyback
                    : ROUTES.buyback.list,
        };
    }

    if (pathname.startsWith('/exchanges') || pathname === ROUTES.reports.exchange) {
        return {
            tabs: [
                { key: ROUTES.exchanges.list, path: ROUTES.exchanges.list, label: 'Quản lý đơn thu đổi' },
                { key: ROUTES.exchanges.new, path: ROUTES.exchanges.new, label: 'Tạo đơn thu đổi' },
                // { key: ROUTES.reports.exchange, path: ROUTES.reports.exchange, label: 'Báo cáo thu đổi' },
            ],
            activePath: pathname === ROUTES.exchanges.new
                ? ROUTES.exchanges.new
                : pathname === ROUTES.reports.exchange
                    ? ROUTES.reports.exchange
                    : ROUTES.exchanges.list,
        };
    }

    return null;
}

function isActiveTab(tabPath: string, activePath: string) {
    return tabPath === activePath;
}

export const Breadcrumb = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const activeGroup = useActiveMenuGroup();

    const nav = useMemo(() => {
        const special = getSpecialTabs(pathname);
        if (special) return special;

        if (activeGroup && activeGroup.children.length >= 2) {
            const activePath = activeGroup.activeChild?.path ?? activeGroup.children[0]?.path ?? '';
            return {
                tabs: activeGroup.children.map((child: ActiveMenuChild) => ({
                    key: child.key,
                    path: child.path,
                    label: child.label,
                })),
                activePath,
            };
        }

        return null;
    }, [activeGroup, pathname]);

    if (!nav || nav.tabs.length === 0) return null;

    return (
        <div
            style={{
                backgroundColor: '#fafafa',
                borderBottom: '1px solid #e8e8e8',
                padding: '0 16px',
                marginBottom: 12,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 32,
                    overflowX: 'auto',
                }}
            >
                {nav.tabs.map((tab) => {
                    const active = isActiveTab(tab.path, nav.activePath);
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => navigate(tab.path)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                color: active ? '#000' : '#595959',
                                fontWeight: active ? 600 : 400,
                                fontSize: 14,
                                lineHeight: '20px',
                                padding: '12px 4px',
                                borderBottom: active ? '2px solid #000' : '2px solid transparent',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                transition: 'all 0.2s ease',
                                userSelect: 'none',
                            }}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
